// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import {
  InventoryOutlined,
  LocalShippingOutlined,
} from '@/constants/hardfin-standard-icons';
import { sortDirectionSchema } from '@/constants/sort';
import {
  DEFAULT_CYCLE_TIME_RANGE_DAYS,
  useReportDateRange,
} from '@/report/hooks/use-report-interval';
import { shipmentSortPropertySchema } from '@/shipment/api/get-shipments';
import { ShipmentType, shipmentStatusSchema } from '@/shipment/api/types';
import { InboundShipmentAnalyticsSection } from '@/shipment/components/inbound-shipment-analytics-section';
import { OutboundShipmentAnalyticsSection } from '@/shipment/components/outbound-shipment-analytics-section';
import { ShipmentListing } from '@/shipment/components/shipment-listing';
import {
  INBOUND_STATUS_OPTIONS,
  OUTBOUND_STATUS_OPTIONS,
} from '@/shipment/constants/status-filter-options';
import {
  ShipmentSearchListing,
  shipmentSearchListingSchema,
} from '@/shipment/hooks/use-shipments';
import { routeDateSchema } from '@/utilities/date-time-schema';
import { searchTermSchema } from '@/utilities/search-term';
import { Stack, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Search schema for the shipments route with type and status as query parameters.
 *
 * The from/to location filter parameters support three independent
 * dimensions per side (explicit location IDs, customer IDs, and company
 * types) so that the same filter UI used on the fulfillment listing can
 * be applied to shipments.
 */
const shipmentsSearchSchema = shipmentSearchListingSchema.extend({
  type: z.enum(['inbound', 'outbound']).default('outbound').catch('outbound'),
  status: shipmentStatusSchema.default('all').catch('all'),
  page: z.number().default(1).catch(1),
  query: searchTermSchema.catch(undefined),
  sort: shipmentSortPropertySchema.optional().catch(undefined),
  direction: sortDirectionSchema.optional().catch(undefined),
  startOn: routeDateSchema,
  endOn: routeDateSchema,
});

/**
 * Route configuration for shipment listing page.
 * Displays shipments with type and status controlled via query parameters.
 */
export const Route = createFileRoute('/_app/shipments/')({
  component: ShipmentsRoute,
  validateSearch: shipmentsSearchSchema,
});

/**
 * Main component for the shipment listing page.
 */
function ShipmentsRoute() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const shipmentType: ShipmentType = search.type;
  const currentStatus = search.status;
  const searchParameters: ShipmentSearchListing = {
    page: search.page,
    query: search.query,
    status: currentStatus === 'all' ? undefined : currentStatus,
    fromLocations: search.fromLocations,
    fromCustomerLocations: search.fromCustomerLocations,
    fromLocationCompanyTypes: search.fromLocationCompanyTypes,
    toLocations: search.toLocations,
    toCustomerLocations: search.toCustomerLocations,
    toLocationCompanyTypes: search.toLocationCompanyTypes,
    needsAttention: search.needsAttention,
    quarantined: search.quarantined,
    sort: search.sort,
    direction: search.direction,
  };
  const { user } = useCurrentUser();
  const cycleTimeDateRange = useReportDateRange({
    start: search.startOn,
    end: search.endOn,
    defaultRangeDays: DEFAULT_CYCLE_TIME_RANGE_DAYS,
  });

  const statusOptions =
    shipmentType === 'inbound'
      ? INBOUND_STATUS_OPTIONS
      : OUTBOUND_STATUS_OPTIONS;

  const pageTitle =
    shipmentType === 'inbound' ? 'Inbound shipments' : 'Outbound shipments';
  const PageIcon =
    shipmentType === 'inbound' ? InventoryOutlined : LocalShippingOutlined;
  const handleCycleTimeDateRangeChange = (dateRange: DateRange<DateTime>) => {
    navigate({
      search: (prev) => ({
        ...prev,
        startOn: dateRange[0]?.toISODate(),
        endOn: dateRange[1]?.toISODate(),
      }),
    });
  };

  return (
    <PageContent>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PageIcon fontSize="small" color="action" />
          <Typography variant="h6">{pageTitle}</Typography>
        </Stack>
        {shipmentType === 'outbound' && (
          <OutboundShipmentAnalyticsSection
            interval={cycleTimeDateRange.interval}
            timezone={user.organization.timezone}
            isDateRangeInvalid={cycleTimeDateRange.isInvalid}
            onDateRangeChange={handleCycleTimeDateRangeChange}
          />
        )}
        {shipmentType === 'inbound' && (
          <InboundShipmentAnalyticsSection
            interval={cycleTimeDateRange.interval}
            timezone={user.organization.timezone}
            isDateRangeInvalid={cycleTimeDateRange.isInvalid}
            onDateRangeChange={handleCycleTimeDateRangeChange}
          />
        )}
        <ShipmentListing
          currentStatus={currentStatus}
          shipmentType={shipmentType}
          statusOptions={statusOptions}
          searchParameters={searchParameters}
          navigateFn={(options) => {
            navigate({
              ...options,
              search: {
                ...options.search,
                startOn: search.startOn,
                endOn: search.endOn,
              },
            });
          }}
        />
      </Stack>
    </PageContent>
  );
}
